import { SettingsPermissions } from "@redotech/account-common/permissions";
import { directMerchantAccess } from "../../settings-catalog/access";
import type {
  SettingGroupDefinition,
  SettingOwner,
  SettingRisk,
} from "../../settings-catalog/types";

export type OrderTrackingSettingsFlatTeamSettingGroupId =
  | "order_tracking_settings.general"
  | "order_tracking_settings.checkout_delivery_estimates"
  | "order_tracking_settings.carrier_mappings"
  | "order_tracking_settings.weekly_email";

const groupOwner: SettingOwner = {
  kind: "teamSettings",
  product: "fulfillment",
};

const groupRisk: SettingRisk = {
  level: "low",
  reason:
    "Order tracking thresholds and delivery monitoring configuration for the team's post-purchase order tracking experience.",
};

export const OrderTrackingSettingsFlatTeamSettingGroups = [
  {
    id: "order_tracking_settings.general",
    title: "General",
    description:
      "Stalled-order thresholds and delivery monitoring settings on the Order tracking settings page.",
    productArea: "fulfillment",
    access: directMerchantAccess(SettingsPermissions.SET_MARKETING_SETTINGS),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "order-tracking" },
    synonyms: [
      "order tracking",
      "stalled orders",
      "delivery monitoring",
      "order tracking settings",
    ],
  },
  {
    id: "order_tracking_settings.checkout_delivery_estimates",
    title: "Checkout delivery estimates",
    description:
      "Controls whether configured estimated delivery dates appear during checkout.",
    productArea: "fulfillment",
    access: directMerchantAccess(SettingsPermissions.SET_MARKETING_SETTINGS),
    owner: groupOwner,
    risk: {
      level: "medium",
      reason:
        "Changes delivery-date information shown to customers during checkout.",
    },
    agentEditability: { mode: "requiresApproval" },
    availability: {
      conditions: [
        {
          type: "evaluator",
          evaluator: "checkoutDeliveryEstimates.configured",
        },
      ],
      unavailableBehavior: "hidden",
      reason:
        "Checkout delivery estimates must be configured for this merchant before the control is available.",
    },
    fullPageTarget: { type: "settings", path: "order-tracking" },
    synonyms: ["checkout delivery estimates", "checkout EDD"],
  },
  {
    id: "order_tracking_settings.carrier_mappings",
    title: "Carrier mappings",
    description:
      "Carrier normalization mappings on the Order tracking settings page.",
    productArea: "fulfillment",
    access: directMerchantAccess(SettingsPermissions.SET_MARKETING_SETTINGS),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "order-tracking" },
    synonyms: ["carrier mappings", "carrier normalization"],
  },
  {
    id: "order_tracking_settings.weekly_email",
    title: "Weekly email update",
    description:
      "Weekly order tracking recap email settings on the Order tracking settings page.",
    productArea: "fulfillment",
    access: directMerchantAccess(SettingsPermissions.SET_MARKETING_SETTINGS),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "order-tracking" },
    synonyms: [
      "weekly email",
      "weekly recap",
      "analytics email",
      "order tracking",
    ],
  },
] as const satisfies readonly SettingGroupDefinition<OrderTrackingSettingsFlatTeamSettingGroupId>[];

export const OrderTrackingSettingsFlatTeamSettingGroupById: Record<
  OrderTrackingSettingsFlatTeamSettingGroupId,
  SettingGroupDefinition<OrderTrackingSettingsFlatTeamSettingGroupId>
> = (() => {
  const map: Partial<
    Record<
      OrderTrackingSettingsFlatTeamSettingGroupId,
      SettingGroupDefinition<OrderTrackingSettingsFlatTeamSettingGroupId>
    >
  > = {};
  for (const group of OrderTrackingSettingsFlatTeamSettingGroups) {
    map[group.id] = group;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return map as Record<
    OrderTrackingSettingsFlatTeamSettingGroupId,
    SettingGroupDefinition<OrderTrackingSettingsFlatTeamSettingGroupId>
  >;
})();
